import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Plus, Sparkle, Play, Trash, PencilSimple, Eye, Globe, Lock,
  SpinnerGap, MagnifyingGlass, CaretRight, CaretLeft, Table,
  Check, FloppyDisk, Lightning, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ago`;
  const hours = Math.floor(diff / 3600000);
  if (hours > 0) return `${hours}h ago`;
  return 'just now';
}

export default function CustomReportsTab() {
  const [reports, setReports] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [filter, setFilter] = useState('all'); // all, mine, public
  const [loading, setLoading] = useState(false);

  // Builder state
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderStep, setBuilderStep] = useState(1); // 1=source, 2=columns+filters, 3=name+save
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [reportFilters, setReportFilters] = useState({});
  const [reportName, setReportName] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [reportLimit, setReportLimit] = useState(100);
  const [visibility, setVisibility] = useState('private');
  const [saving, setSaving] = useState(false);

  // AI state
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Run/view state
  const [runResult, setRunResult] = useState(null);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [running, setRunning] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { visibility: filter } : {};
      const res = await API.get('/reports/custom', { params });
      setReports(res.data.items);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [filter]);

  const loadSources = useCallback(async () => {
    try {
      const res = await API.get('/reports/custom/data-sources');
      setDataSources(res.data.sources);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadSources(); }, [loadSources]);

  const resetBuilder = () => {
    setBuilderStep(1);
    setSelectedSource(null);
    setSelectedColumns([]);
    setReportFilters({});
    setReportName('');
    setReportDesc('');
    setSortBy('');
    setSortOrder('desc');
    setReportLimit(100);
    setVisibility('private');
    setEditingReport(null);
  };

  const handleSave = async () => {
    if (!reportName.trim() || !selectedSource) return;
    setSaving(true);
    try {
      const payload = {
        name: reportName,
        description: reportDesc,
        data_source: selectedSource.id,
        columns: selectedColumns,
        filters: reportFilters,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: reportLimit,
        visibility,
      };

      if (editingReport) {
        await API.put(`/reports/custom/${editingReport.id}`, payload);
        toast.success('Report updated');
      } else {
        await API.post('/reports/custom', payload);
        toast.success('Report created');
      }
      setShowBuilder(false);
      resetBuilder();
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save report');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await API.delete(`/reports/custom/${id}`);
      toast.success('Report deleted');
      loadReports();
    } catch { toast.error('Failed to delete'); }
  };

  const handleRun = async (report) => {
    setRunning(true);
    setRunResult(null);
    setShowRunDialog(true);
    try {
      const res = await API.post(`/reports/custom/${report.id}/run`);
      setRunResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run report');
      setShowRunDialog(false);
    } finally { setRunning(false); }
  };

  const handleEdit = (report) => {
    const source = dataSources.find(s => s.id === report.data_source);
    setEditingReport(report);
    setSelectedSource(source);
    setSelectedColumns(report.columns || []);
    setReportFilters(report.filters || {});
    setReportName(report.name);
    setReportDesc(report.description || '');
    setSortBy(report.sort_by || '');
    setSortOrder(report.sort_order || 'desc');
    setReportLimit(report.limit || 100);
    setVisibility(report.visibility || 'private');
    setBuilderStep(2);
    setShowBuilder(true);
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    setAiResult(null);
    try {
      const res = await API.post('/reports/custom/ai-generate', { prompt: aiPrompt });
      setAiResult(res.data.config);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally { setAiGenerating(false); }
  };

  const handleApplyAiResult = () => {
    if (!aiResult) return;
    const source = dataSources.find(s => s.id === aiResult.data_source);
    setSelectedSource(source);
    setSelectedColumns(aiResult.columns || []);
    setReportFilters(aiResult.filters || {});
    setReportName(aiResult.name || '');
    setReportDesc(aiResult.description || '');
    setSortBy(aiResult.sort_by || '');
    setSortOrder(aiResult.sort_order || 'desc');
    setReportLimit(aiResult.limit || 100);
    setShowAiDialog(false);
    setAiResult(null);
    setAiPrompt('');
    setBuilderStep(2);
    setShowBuilder(true);
  };

  const toggleColumn = (field) => {
    setSelectedColumns(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const handleExportCsv = () => {
    if (!runResult?.rows?.length) return;
    const cols = runResult.columns;
    const header = cols.join(',');
    const rows = runResult.rows.map(r => cols.map(c => {
      const val = String(r[c] ?? '').replace(/"/g, '""');
      return `"${val}"`;
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${runResult.report?.name || 'report'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sourceFields = selectedSource?.fields || [];

  return (
    <div className="space-y-4" data-testid="custom-reports-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Custom Reports</h2>
          <p className="text-xs text-navy-500">Build, save, and share reports on any data in the platform</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowAiDialog(true); setAiPrompt(''); setAiResult(null); }} className="rounded-sm text-xs" data-testid="ai-report-btn">
            <Sparkle size={14} className="mr-1.5" /> AI Report Builder
          </Button>
          <Button size="sm" onClick={() => { resetBuilder(); setShowBuilder(true); }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs" data-testid="new-report-btn">
            <Plus size={14} className="mr-1.5" /> New Report
          </Button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2">
        {[{ value: 'all', label: 'All Reports' }, { value: 'mine', label: 'My Reports' }, { value: 'public', label: 'Public' }].map(f => (
          <button key={f.value} data-testid={`report-filter-${f.value}`}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
              filter === f.value ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-navy-600 border-slate-200 hover:border-orange-300'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={32} className="animate-spin text-navy-400" /></div>
      ) : reports.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
          <Table size={40} className="text-navy-300 mx-auto" />
          <p className="text-sm text-navy-500 mt-3">No custom reports yet</p>
          <p className="text-xs text-navy-400 mt-1">Create a new report or use AI to generate one</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="reports-list">
          {reports.map(report => (
            <div key={report.id} data-testid={`report-card-${report.id}`}
              className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className={`h-1 ${report.visibility === 'public' ? 'bg-orange-500' : 'bg-navy-600'}`} />
              <div className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-navy-900 truncate">{report.name}</p>
                    {report.ai_generated && <Badge className="bg-purple-100 text-purple-700 text-[9px]"><Sparkle size={10} className="mr-0.5" />AI</Badge>}
                    <Badge className={`text-[9px] ${report.visibility === 'public' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-navy-600'}`}>
                      {report.visibility === 'public' ? <><Globe size={10} className="mr-0.5" />Public</> : <><Lock size={10} className="mr-0.5" />Private</>}
                    </Badge>
                  </div>
                  <p className="text-xs text-navy-500 mt-0.5 truncate">{report.description || 'No description'}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-navy-400">
                    <span>Source: <strong className="text-navy-600">{report.data_source}</strong></span>
                    <span>By: {report.created_by_name}</span>
                    <span>{timeAgo(report.created_at)}</span>
                    <span>Runs: {report.run_count}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleRun(report)} className="h-8 px-2" data-testid={`run-report-${report.id}`}>
                    <Play size={14} weight="fill" className="text-emerald-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(report)} className="h-8 px-2" data-testid={`edit-report-${report.id}`}>
                    <PencilSimple size={14} className="text-navy-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(report.id)} className="h-8 px-2" data-testid={`delete-report-${report.id}`}>
                    <Trash size={14} className="text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report Builder Dialog */}
      <Dialog open={showBuilder} onOpenChange={(open) => { if (!open) { setShowBuilder(false); resetBuilder(); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="report-builder-dialog" aria-describedby="builder-desc">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editingReport ? 'Edit Report' : 'Build a Report'}</DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 py-2" data-testid="builder-steps">
            {['Data Source', 'Columns & Filters', 'Save'].map((label, idx) => {
              const step = idx + 1;
              const isActive = step === builderStep;
              const isDone = step < builderStep;
              return (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-orange-500 text-white' : 'bg-slate-200 text-navy-500'
                  }`}>{isDone ? <Check size={12} weight="bold" /> : step}</div>
                  <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-orange-600' : isDone ? 'text-emerald-600' : 'text-navy-400'}`}>{label}</span>
                  {idx < 2 && <div className={`w-8 h-0.5 ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                </div>
              );
            })}
          </div>

          <div id="builder-desc" className="flex-1 overflow-y-auto space-y-4">
            {/* STEP 1: Source */}
            {builderStep === 1 && (
              <div data-testid="builder-step-1">
                <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-3">Select a Data Source</p>
                <div className="grid gap-2 max-h-[400px] overflow-y-auto">
                  {dataSources.map(src => (
                    <button key={src.id} data-testid={`source-${src.id}`}
                      onClick={() => { setSelectedSource(src); setSelectedColumns(src.fields.slice(0, 5)); }}
                      className={`w-full text-left px-4 py-3 rounded-sm border transition-colors ${
                        selectedSource?.id === src.id ? 'bg-orange-50 border-orange-300' : 'bg-white border-slate-200 hover:border-orange-200'
                      }`}>
                      <p className="text-sm font-medium text-navy-900">{src.label}</p>
                      <p className="text-xs text-navy-500">{src.description}</p>
                      <p className="text-[10px] text-navy-400 mt-1">{src.fields.length} fields: {src.fields.slice(0, 4).join(', ')}...</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2: Columns & Filters */}
            {builderStep === 2 && selectedSource && (
              <div data-testid="builder-step-2">
                <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">
                  Columns <span className="text-navy-400 font-normal">({selectedColumns.length} selected)</span>
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                  {sourceFields.map(field => (
                    <label key={field} className="flex items-center gap-2 px-3 py-2 rounded-sm border border-slate-200 hover:bg-slate-50 cursor-pointer text-xs">
                      <Checkbox checked={selectedColumns.includes(field)} onCheckedChange={() => toggleColumn(field)} />
                      <span className="text-navy-700 font-medium">{field}</span>
                    </label>
                  ))}
                </div>

                <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">Filters (optional)</p>
                <div className="space-y-2 mb-5">
                  {sourceFields.map(field => (
                    <div key={field} className="flex items-center gap-2">
                      <span className="text-xs text-navy-600 w-36 shrink-0 truncate">{field}</span>
                      <Input className="h-8 text-xs" placeholder={`Filter ${field}...`}
                        value={reportFilters[field] || ''} data-testid={`filter-${field}`}
                        onChange={(e) => setReportFilters(prev => ({ ...prev, [field]: e.target.value || undefined }))} />
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] font-semibold text-navy-600 uppercase tracking-widest mb-1">Sort By</p>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Default" /></SelectTrigger>
                      <SelectContent>{sourceFields.map(f => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-navy-600 uppercase tracking-widest mb-1">Order</p>
                    <Select value={sortOrder} onValueChange={setSortOrder}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc" className="text-xs">Ascending</SelectItem>
                        <SelectItem value="desc" className="text-xs">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-navy-600 uppercase tracking-widest mb-1">Limit</p>
                    <Input type="number" className="h-8 text-xs" value={reportLimit} min={1} max={1000}
                      onChange={(e) => setReportLimit(Math.min(parseInt(e.target.value) || 100, 1000))} />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Name & Save */}
            {builderStep === 3 && (
              <div data-testid="builder-step-3">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-1">Report Name *</p>
                    <Input data-testid="report-name-input" value={reportName} onChange={(e) => setReportName(e.target.value)}
                      placeholder="e.g., Active Identities by Department" className="text-sm" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-1">Description</p>
                    <textarea data-testid="report-desc-input" value={reportDesc} onChange={(e) => setReportDesc(e.target.value)}
                      placeholder="Brief description of what this report shows..."
                      className="w-full border border-slate-200 rounded-sm p-3 text-sm h-20 focus:ring-2 focus:ring-orange-500 outline-none resize-none" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">Visibility</p>
                    <div className="flex gap-2">
                      <button data-testid="visibility-private" onClick={() => setVisibility('private')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                          visibility === 'private' ? 'bg-navy-600 text-white border-navy-600' : 'bg-white text-navy-700 border-slate-200'
                        }`}><Lock size={16} /> Private (only you)</button>
                      <button data-testid="visibility-public" onClick={() => setVisibility('public')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                          visibility === 'public' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-navy-700 border-slate-200'
                        }`}><Globe size={16} /> Public (all users)</button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs space-y-1.5">
                    <p><strong className="text-navy-700">Source:</strong> {selectedSource?.label}</p>
                    <p><strong className="text-navy-700">Columns:</strong> {selectedColumns.join(', ')}</p>
                    {Object.entries(reportFilters).filter(([,v]) => v).length > 0 && (
                      <p><strong className="text-navy-700">Filters:</strong> {Object.entries(reportFilters).filter(([,v]) => v).map(([k,v]) => `${k}="${v}"`).join(', ')}</p>
                    )}
                    <p><strong className="text-navy-700">Sort:</strong> {sortBy || 'default'} ({sortOrder}) &middot; Limit: {reportLimit}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between pt-3 border-t border-slate-200">
            <div>
              {builderStep > 1 && (
                <Button variant="outline" onClick={() => setBuilderStep(s => s - 1)} className="rounded-sm" data-testid="builder-back">
                  <CaretLeft size={14} className="mr-1" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {builderStep < 3 && (
                <Button onClick={() => setBuilderStep(s => s + 1)} disabled={!selectedSource}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="builder-next">
                  Next <CaretRight size={14} className="ml-1" />
                </Button>
              )}
              {builderStep === 3 && (
                <Button onClick={handleSave} disabled={!reportName.trim() || saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="builder-save">
                  {saving ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <FloppyDisk size={14} className="mr-1.5" />}
                  {editingReport ? 'Update Report' : 'Save Report'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Report Builder Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-lg" data-testid="ai-report-dialog" aria-describedby="ai-report-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Sparkle size={20} weight="fill" className="text-purple-500" />
              AI Report Builder
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4" id="ai-report-desc">
            <p className="text-sm text-navy-600">Describe the report you want and AI will configure it for you.</p>

            <textarea data-testid="ai-prompt-input" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g., Show me all active identities in the Engineering department sorted by name"
              className="w-full border border-slate-200 rounded-sm p-3 text-sm h-24 focus:ring-2 focus:ring-purple-500 outline-none resize-none" />

            <div className="flex flex-wrap gap-1.5">
              {['Active identities without a manager', 'All roles with more than 5 assignments', 'Pending access requests this month', 'SoD violations by identity', 'JIT sessions in the last 7 days'].map(q => (
                <button key={q} onClick={() => setAiPrompt(q)}
                  className="text-[10px] px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-sm hover:bg-purple-100 transition-colors">
                  {q}
                </button>
              ))}
            </div>

            <Button onClick={handleAiGenerate} disabled={!aiPrompt.trim() || aiGenerating}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-sm" data-testid="ai-generate-btn">
              {aiGenerating ? <SpinnerGap size={16} className="mr-2 animate-spin" /> : <Lightning size={16} className="mr-2" />}
              Generate Report Configuration
            </Button>

            {/* AI Result Preview */}
            {aiResult && (
              <div className="bg-purple-50 border border-purple-200 rounded-sm p-4 space-y-2" data-testid="ai-result-preview">
                <p className="text-sm font-semibold text-purple-900">{aiResult.name}</p>
                <p className="text-xs text-purple-700">{aiResult.description}</p>
                <div className="text-[10px] text-purple-600 space-y-1">
                  <p><strong>Source:</strong> {aiResult.data_source}</p>
                  <p><strong>Columns:</strong> {aiResult.columns?.join(', ')}</p>
                  {aiResult.filters && Object.keys(aiResult.filters).length > 0 && (
                    <p><strong>Filters:</strong> {JSON.stringify(aiResult.filters)}</p>
                  )}
                  <p><strong>Sort:</strong> {aiResult.sort_by} ({aiResult.sort_order}) &middot; Limit: {aiResult.limit}</p>
                </div>
                <Button onClick={handleApplyAiResult} className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-sm mt-2" data-testid="ai-apply-btn">
                  <Check size={14} className="mr-1.5" /> Use This Configuration
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Run Results Dialog */}
      <Dialog open={showRunDialog} onOpenChange={setShowRunDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="run-results-dialog" aria-describedby="run-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Table size={20} className="text-navy-600" />
              {runResult?.report?.name || 'Report Results'}
            </DialogTitle>
          </DialogHeader>

          {running ? (
            <div className="flex justify-center py-16"><SpinnerGap size={32} className="animate-spin text-navy-400" /></div>
          ) : runResult ? (
            <>
              <div className="flex items-center justify-between py-2 shrink-0" id="run-desc">
                <p className="text-xs text-navy-500">{runResult.total} rows from <strong>{runResult.report?.data_source}</strong></p>
                <Button variant="outline" size="sm" className="rounded-sm text-xs h-8" onClick={handleExportCsv} data-testid="export-csv-btn">
                  Export CSV
                </Button>
              </div>
              <div className="flex-1 overflow-auto border border-slate-200 rounded-sm">
                <table className="w-full text-xs" data-testid="run-results-table">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {runResult.columns.map(col => (
                        <th key={col} className="text-left px-3 py-2.5 text-[10px] font-bold text-navy-500 uppercase tracking-widest border-b border-slate-200 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {runResult.rows.map((row, idx) => (
                      <tr key={row.id || idx} className="hover:bg-slate-50">
                        {runResult.columns.map(col => (
                          <td key={col} className="px-3 py-2 text-navy-700 whitespace-nowrap max-w-[200px] truncate">{String(row[col] ?? '-')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
